<template>
  <section class="admin-agent-dashboard py-6 px-4">
    <b-container fluid>
      <b-card class="border-0 shadow-sm">
        <template #header>
          <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center">
            <div>
              <h2 class="h4 text-primary mb-1">Admin agent dashboard</h2>
              <p class="mb-0" style="color: #495057 !important;">
                Analyze your assigned agents by filtering date range, agent and insurance type.
              </p>
            </div>
            <b-button
              variant="primary"
              class="mt-3 mt-md-0"
              :disabled="loading"
              @click="fetchDashboardData"
            >
              <span v-if="!loading">Search</span>
              <b-spinner v-else small label="Loading..."></b-spinner>
            </b-button>
          </div>
        </template>

        <b-form @submit.prevent="fetchDashboardData">
          <b-row>
            <b-col cols="12" md="3" class="mb-3">
              <label class="font-weight-semibold">Start date</label>
              <flat-pickr
                v-model="filters.start_date"
                :config="dateConfig"
                class="form-control"
                placeholder="YYYY-MM-DD"
              />
            </b-col>

            <b-col cols="12" md="3" class="mb-3">
              <label class="font-weight-semibold">End date</label>
              <flat-pickr
                v-model="filters.end_date"
                :config="dateConfig"
                class="form-control"
                placeholder="YYYY-MM-DD"
              />
            </b-col>

            <b-col cols="12" md="3" class="mb-3">
              <label class="font-weight-semibold d-block">Agent</label>
              <treeselect
                v-model="filters.agent_id"
                :options="agentOptions"
                :normalizer="agentNormalizer"
                :multiple="false"
                :clearable="true"
                :disable-branch-nodes="true"
                placeholder="Select an agent (optional)"
                no-options-text="No agents available"
              />
            </b-col>

            <b-col cols="12" md="3" class="mb-3">
              <label class="font-weight-semibold d-block">Insurance type</label>
              <treeselect
                v-model="filters.type_insurance"
                :options="insuranceTypeOptions"
                :normalizer="insuranceTypeNormalizer"
                :multiple="false"
                :clearable="true"
                :disable-branch-nodes="true"
                placeholder="Select an insurance type"
                no-options-text="No insurance types available"
              />
            </b-col>
          </b-row>

          <b-button variant="primary" type="submit" class="d-none">
            Search
          </b-button>
        </b-form>

        <b-alert
          variant="danger"
          :show="Boolean(errorMessage)"
          dismissible
          @dismissed="errorMessage = null"
          class="mt-3"
        >
          {{ errorMessage }}
        </b-alert>

        <div class="mt-4">
          <div v-if="loading" class="d-flex align-items-center">
            <SpinnerLoading />
          </div>

          <div v-else>
            <b-row class="mb-4">
              <b-col
                v-for="card in summaryCards"
                :key="card.key"
                cols="12"
                md="3"
                class="mb-3"
              >
                <div class="summary-card p-3 rounded shadow-sm h-100">
                  <p class="mb-1 font-weight-semibold" style="color: #495057 !important;">
                    {{ card.label }}
                  </p>
                  <p class="h4 text-primary mb-0">{{ card.display }}</p>
                </div>
              </b-col>
            </b-row>

            <b-card class="border-0 shadow-sm mb-4">
              <template #header>
                <div class="d-flex justify-content-between align-items-center">
                  <h3 class="h5 mb-0 text-primary">Visit distribution</h3>
                  <span class="small" style="color: #495057 !important;">
                    Total visits: {{ formatNumber(totalVisitCount, true) }}
                  </span>
                </div>
              </template>
              <div v-if="visitCountChartSeries[0].data.length">
                <apexchart
                  type="bar"
                  height="320"
                  :options="visitCountChartOptions"
                  :series="visitCountChartSeries"
                />
              </div>
              <b-alert v-else variant="light" show>
                No visit data available for the chart.
              </b-alert>
            </b-card>

            <b-card class="border-0 shadow-sm mb-4">
              <template #header>
                <h3 class="h5 mb-0 text-primary">Patients by insurance type</h3>
              </template>
              <div v-if="patientsByInsuranceChartSeries[0].data.length">
                <apexchart
                  type="bar"
                  height="360"
                  :options="patientsByInsuranceChartOptions"
                  :series="patientsByInsuranceChartSeries"
                />
              </div>
              <b-alert v-else variant="light" show>
                No insurance distribution data available.
              </b-alert>
            </b-card>

            <b-card class="border-0 shadow-sm mb-4">
              <template #header>
                <div class="d-flex justify-content-between align-items-center">
                  <h3 class="h5 mb-0 text-primary">Visit totals overview</h3>
                  <span class="small" style="color: #495057 !important;">
                    Comparison of NP, NC and overall totals.
                  </span>
                </div>
              </template>
              <div v-if="hasVisitTotalsData">
                <b-row>
                  <b-col cols="12" md="7" class="mb-3 mb-md-0">
                    <apexchart
                      type="pie"
                      height="360"
                      :options="visitTotalsPieOptions"
                      :series="visitTotalsPieSeries"
                    />
                  </b-col>
                  <b-col
                    cols="12"
                    md="5"
                    class="d-flex flex-column justify-content-center"
                  >
                    <div
                      v-for="item in visitTotalsLegend"
                      :key="item.label"
                      class="visit-totals-legend-item d-flex align-items-center mb-3"
                    >
                      <span
                        class="visit-totals-legend-dot mr-3"
                        :style="{ backgroundColor: item.color }"
                      ></span>
                      <div>
                        <p class="mb-1 font-weight-semibold text-dark">
                          {{ item.label }}
                        </p>
                        <p class="mb-0 text-primary font-weight-bold" style="font-size: 1.5rem;">
                          {{ formatNumber(item.value) }}
                        </p>
                      </div>
                    </div>
                  </b-col>
                </b-row>
              </div>
              <b-alert v-else variant="light" show>
                No totals available to display.
              </b-alert>
            </b-card>

            <b-card class="border-0 shadow-sm">
              <template #header>
                <div class="d-flex justify-content-between align-items-center">
                  <h3 class="h5 mb-0 text-primary">Patients and visits</h3>
                  <span class="small" style="color: #495057 !important;">
                    Records found: {{ filteredPatients.length }}
                  </span>
                </div>
              </template>

              <b-form-group
                label="Search patient"
                label-for="patient-search"
                label-class="font-weight-semibold"
              >
                <b-form-input
                  id="patient-search"
                  v-model="patientSearch"
                  placeholder="Name, phone, insurance or agent"
                />
              </b-form-group>

              <b-alert v-if="!patientRows.length" show variant="info">
                No records found for the selected filters.
              </b-alert>

              <template v-else>
                <b-table
                  :fields="tableFields"
                  :items="filteredPatients"
                  responsive="sm"
                  head-variant="light"
                  class="agent-data-table"
                  show-empty
                  empty-text="No patients match the search."
                >
                  <template #cell(agentName)="row">
                    {{ row.item.agentName || "Unknown Agent" }}
                  </template>
                  <template #cell(total_visit_amount)="row">
                    {{
                      formatNumber(
                        (row.item.visits_count || 0) * agentPayRate
                      )
                    }}
                  </template>
                  <template #cell(actions)="row">
                    <b-button
                      size="sm"
                      variant="outline-primary"
                      @click="row.toggleDetails"
                    >
                      {{ row.detailsShowing ? "Hide" : "View" }} visits
                    </b-button>
                  </template>
                  <template #row-details="row">
                    <b-table
                      :items="row.item.visits"
                      :fields="visitDetailFields"
                      small
                      bordered
                      responsive="sm"
                      head-variant="light"
                      class="mb-2"
                      show-empty
                      empty-text="No visits registered."
                    >
                      <template #cell(total)="visitRow">
                        {{ formatNumber(visitRow.item.total) }}
                      </template>
                    </b-table>
                  </template>
                </b-table>
              </template>
            </b-card>

          </div>
        </div>
      </b-card>

      <agency-plan-panel v-if="isAgencyAdmin" />
    </b-container>
  </section>
</template>

<script>
import {
  BAlert,
  BButton,
  BCard,
  BCol,
  BContainer,
  BForm,
  BFormGroup,
  BFormInput,
  BRow,
  BSpinner,
  BTable,
} from "bootstrap-vue";
import flatPickr from "vue-flatpickr-component";
import VueApexCharts from "vue-apexcharts";
import { mapState } from "vuex";
import { ADMIN_AGENT_PAY_KEY } from "@/core/services/store/auth.module";
import adminAgentDashboardService from "@/core/services/api/admin/adminAgentDashboard";
import insuranceNameService from "@/core/services/apiInsurance/admin/consults/insuranceName";
import SpinnerLoading from "@/components/SpinnerLoading.vue";
import AgencyPlanPanel from "@/view/pages/agentPlan/dashboard/AgencyPlanPanel.vue";

export default {
  name: "AdminAgentDashboard",
  components: {
    BAlert,
    BButton,
    BCard,
    BCol,
    BContainer,
    BForm,
    BFormGroup,
    BFormInput,
    BRow,
    BSpinner,
    BTable,
    flatPickr,
    apexchart: VueApexCharts,
    SpinnerLoading,
    AgencyPlanPanel,
  },
  data() {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    return {
      loading: false,
      errorMessage: null,
      rawDashboardResponse: null,
      dashboardData: null,
      rawPatients: [],
      patientRows: [],
      visitCounts: [],
      totals: {},
      patientsByInsurance: [],
      filters: {
        start_date: firstDayOfMonth.toISOString().slice(0, 10),
        end_date: today.toISOString().slice(0, 10),
        agent_id: null,
        type_insurance: null,
      },
      agentOptions: [],
      insuranceTypeOptions: [],
      patientSearch: "",
      shouldAutoFetch: false,
      dateConfig: {
        dateFormat: "Y-m-d",
        allowInput: true,
      },
      visitCountChartOptions: {
        chart: {
          type: "bar",
          toolbar: { show: false },
        },
        dataLabels: {
          enabled: true,
        },
        plotOptions: {
          bar: {
            columnWidth: "45%",
            borderRadius: 4,
          },
        },
        xaxis: {
          categories: [],
          labels: {
            rotate: -45,
          },
        },
        yaxis: {
          labels: {
            formatter: (value) => Math.round(value),
          },
        },
        colors: ["#1E88E5"],
      },
      visitCountChartSeries: [
        {
          name: "Visits",
          data: [],
        },
      ],
      patientsByInsuranceChartOptions: {
        chart: {
          type: "bar",
          stacked: true,
          toolbar: { show: false },
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "45%",
            borderRadius: 4,
          },
        },
        xaxis: {
          categories: [],
          labels: {
            rotate: -45,
          },
        },
        yaxis: {
          labels: {
            formatter: (value) => Math.round(value),
          },
        },
        legend: {
          position: "top",
        },
        dataLabels: {
          enabled: true,
        },
        fill: {
          opacity: 1,
        },
        colors: ["#1E88E5", "#26C6DA"],
      },
      patientsByInsuranceChartSeries: [
        {
          name: "New patients (NP)",
          data: [],
        },
        {
          name: "Returning patients (NC)",
          data: [],
        },
      ],
      visitTotalsPieOptions: {
        chart: {
          type: "pie",
        },
        labels: [
          "Returning patients (NC)",
          "New patients (NP)",
          "Total revenue",
        ],
        colors: ["#26C6DA", "#1E88E5", "#7E57C2"],
        legend: {
          show: false,
        },
        dataLabels: {
          enabled: true,
          formatter(val, opts) {
            const seriesVal =
              opts?.w?.globals?.series?.[opts.seriesIndex] || 0;
            return new Intl.NumberFormat("es-US", {
              style: "currency",
              currency: "USD",
              minimumFractionDigits: 2,
            }).format(seriesVal);
          },
        },
        tooltip: {
          y: {
            formatter: (value) =>
              new Intl.NumberFormat("es-US", {
                style: "currency",
                currency: "USD",
                minimumFractionDigits: 2,
              }).format(value || 0),
          },
        },
      },
      visitDetailFields: [
        { key: "visit_date", label: "Date" },
        { key: "facility", label: "Facility" },
        { key: "type_membership", label: "Type visit" },
        { key: "type_insurance", label: "Insurance" },
        { key: "total", label: "Total" },
      ],
    };
  },
  computed: {
    ...mapState({
      auth: (state) => state.auth || {},
    }),
    // agency-plan/dashboard solo responde al rol agent admin (8); un superadmin que abra esta página recibe 403.
    isAgencyAdmin() {
      try {
        const roles = JSON.parse(localStorage.getItem("userData") || "{}").roles || [];
        return Number(roles[0] && roles[0].id) === 8;
      } catch (error) {
        return false;
      }
    },
    tableFields() {
      return [
        { key: "agentName", label: "Agent", sortable: true },
        { key: "fullName", label: "Patient", sortable: true },
        { key: "phone", label: "Phone" },
        { key: "typeInsuranceLabel", label: "Insurance type" },
        { key: "date_birth", label: "Birthday" },
        { key: "visits_count", label: "Visits", sortable: true },
        { key: "total_visit_amount", label: "Win total" },
        { key: "actions", label: "Detail", class: "text-center" },
      ];
    },
    filteredPatients() {
      if (!this.patientSearch) return this.patientRows;
      const search = this.patientSearch.toLowerCase();
      return this.patientRows.filter((patient) =>
        [patient.fullName, patient.phone, patient.typeInsuranceLabel, patient.agentName]
          .filter(Boolean)
          .some((value) =>
            value.toString().toLowerCase().includes(search)
          )
      );
    },
    insuranceTypeMap() {
      return this.insuranceTypeOptions.reduce((map, type) => {
        map[type.id] = type.name;
        return map;
      }, {});
    },
    summaryCards() {
      return [
        {
          key: "total_patients",
          label: "Patients managed",
          value: this.patientRows.length,
          display: this.formatNumber(this.patientRows.length, true),
        },
        {
          key: "total_visits",
          label: "Total visits",
          value: this.totalVisitCount,
          display: this.formatNumber(this.totalVisitCount, true),
        },
      ];
    },
    selectedAgent() {
      const agentId = Number(this.filters.agent_id);
      if (!agentId) return null;
      return (
        this.agentOptions.find((agent) => Number(agent.id) === agentId) || null
      );
    },
    loggedAdminAgentPayload() {
      return this.auth?.user || {};
    },
    agentPayRate() {
      const localUserData = this.getLocalStorageUserData();
      const candidates = [
        this.loggedAdminAgentPayload?.user?.pay_to_visit,
        this.loggedAdminAgentPayload?.data?.user?.pay_to_visit,
        this.loggedAdminAgentPayload?.pay_to_visit,
        localUserData?.pay_to_visit,
      ];
      for (const candidate of candidates) {
        const numeric = Number(candidate);
        if (Number.isFinite(numeric) && numeric >= 0) {
          return numeric;
        }
      }
      if (typeof window !== "undefined") {
        const storedValue = Number(
          window.localStorage.getItem(ADMIN_AGENT_PAY_KEY)
        );
        if (Number.isFinite(storedValue) && storedValue >= 0) {
          return storedValue;
        }
      }
      return 0;
    },
    totalVisitCount() {
      return this.patientRows.reduce((total, patient) => {
        const visits = Array.isArray(patient.visits) ? patient.visits : [];
        return total + visits.length;
      }, 0);
    },
    npVisitCount() {
      return this.patientRows.reduce((count, patient) => {
        const visits = Array.isArray(patient.visits) ? patient.visits : [];
        return (
          count +
          visits.filter((visit) => this.isMembershipNP(visit.type_membership))
            .length
        );
      }, 0);
    },
    ncVisitCount() {
      return this.patientRows.reduce((count, patient) => {
        const visits = Array.isArray(patient.visits) ? patient.visits : [];
        return (
          count +
          visits.filter((visit) => this.isMembershipNC(visit.type_membership))
            .length
        );
      }, 0);
    },
    npTotalValue() {
      return this.patientRows.reduce((sum, patient) => {
        const visits = Array.isArray(patient.visits) ? patient.visits : [];
        return (
          sum +
          visits
            .filter((visit) => this.isMembershipNP(visit.type_membership))
            .reduce(
              (acc, visit) => acc + (Number(visit.total) || 0),
              0
            )
        );
      }, 0);
    },
    ncTotalValue() {
      return this.patientRows.reduce((sum, patient) => {
        const visits = Array.isArray(patient.visits) ? patient.visits : [];
        return (
          sum +
          visits
            .filter((visit) => this.isMembershipNC(visit.type_membership))
            .reduce(
              (acc, visit) => acc + (Number(visit.total) || 0),
              0
            )
        );
      }, 0);
    },
    winTotalValue() {
      return this.npTotalValue + this.ncTotalValue;
    },
    visitTotalsPieSeries() {
      return [this.ncTotalValue, this.npTotalValue, this.winTotalValue];
    },
    hasVisitTotalsData() {
      return this.visitTotalsPieSeries.some((value) => Number(value) > 0);
    },
    visitTotalsLegend() {
      const colors = this.visitTotalsPieOptions.colors || [];
      return [
        {
          label: "Returning patients (NC)",
          value: this.ncTotalValue,
          color: colors[0] || "#26C6DA",
        },
        {
          label: "New patients (NP)",
          value: this.npTotalValue,
          color: colors[1] || "#1E88E5",
        },
        {
          label: "Total revenue",
          value: this.winTotalValue,
          color: colors[2] || "#7E57C2",
        },
      ];
    },
    formattedRawResponse() {
      if (!this.rawDashboardResponse) return "No data available.";
      try {
        return JSON.stringify(this.rawDashboardResponse, null, 2);
      } catch (error) {
        return String(this.rawDashboardResponse);
      }
    },
  },
  methods: {
    getLocalStorageUserData() {
      if (typeof window === "undefined") return null;
      const raw = window.localStorage.getItem("userData");
      if (!raw) return null;
      try {
        const parsed = JSON.parse(raw);
        return parsed;
      } catch (error) {
        return null;
      }
    },
    agentNormalizer(node) {
      const parts = [node.name, node.lastname].filter(Boolean);
      return {
        id: node.id,
        label: parts.length ? parts.join(" ") : `Agent ${node.id}`,
        isDisabled: false,
      };
    },
    insuranceTypeNormalizer(node) {
      return {
        id: node.id,
        label: node.name || `Type ${node.id}`,
        isDisabled: false,
      };
    },
    async loadAgentOptions() {
      try {
        const response = await adminAgentDashboardService.listAgents();
        const list = Array.isArray(response?.data) ? response.data : response;
        this.agentOptions = Array.isArray(list)
          ? list.map((agent) => ({
              id: agent.id,
              name: agent.name,
              lastname: agent.lastname,
              pay_to_visit: Number(agent.pay_to_visit) || 0,
            }))
          : [];
      } catch (error) {
        this.agentOptions = [];
        this.filters.agent_id = null;
        this.errorMessage =
          "We could not load the agent list for the admin.";
      }
    },
    async loadInsuranceTypes() {
      try {
        const response = await insuranceNameService.visitList(100);
        const types = Array.isArray(response?.data) ? response.data : [];
        this.insuranceTypeOptions = types;
        this.refreshPatientRows();
      } catch (error) {
        this.insuranceTypeOptions = [];
      }
    },
    buildFiltersPayload() {
      const payload = {};
      Object.entries(this.filters).forEach(([key, value]) => {
        if (value !== null && value !== "" && value !== undefined) {
          payload[key] = value;
        }
      });
      if (payload.agent_id) {
        payload.agent_id = Number(payload.agent_id);
      }
      if (payload.type_insurance) {
        payload.type_insurance = Number(payload.type_insurance);
      }
      return payload;
    },
    async fetchDashboardData() {
      if (!this.filters.start_date || !this.filters.end_date) {
        this.errorMessage = "Start and end dates are required.";
        return;
      }

      const payload = this.buildFiltersPayload();

      this.loading = true;
      this.errorMessage = null;

      try {
        const response = await adminAgentDashboardService.getDashboardData(
          payload
        );
        this.rawDashboardResponse = response;
        const dataBlock = response?.data || {};
        this.dashboardData = dataBlock;
        this.rawPatients = Array.isArray(dataBlock.patients)
          ? dataBlock.patients
          : [];
        this.visitCounts = this.filterMembershipCounts(
          Array.isArray(dataBlock.visit_counts) ? dataBlock.visit_counts : []
        );
        this.totals = dataBlock.totals || {};
        this.patientsByInsurance = Array.isArray(
          dataBlock.all_patients_by_insurance
        )
          ? dataBlock.all_patients_by_insurance
          : [];

        this.refreshPatientRows();
        this.updateChartData();
        this.updatePatientsByInsuranceChart();
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.message ||
          "We could not fetch the admin-agent dashboard information.";
        this.rawPatients = [];
        this.patientRows = [];
        this.visitCounts = [];
        this.totals = {};
        this.patientsByInsurance = [];
        this.rawDashboardResponse = null;
        this.updateChartData();
        this.updatePatientsByInsuranceChart();
      } finally {
        this.loading = false;
      }
    },
    refreshPatientRows() {
      this.patientRows = this.enrichPatients(this.rawPatients);
    },
    enrichPatients(patients = []) {
      return patients
        .map((patient) => {
          const filteredVisits = (Array.isArray(patient.visits)
            ? patient.visits
            : []
          ).filter(
            (visit) =>
              this.isMembershipNP(visit.type_membership) ||
              this.isMembershipNC(visit.type_membership)
          );

          if (!filteredVisits.length) return null;

          // Obtener el pay_to_visit del agente desde agent_insurance o del admin logueado
          const agentPayToVisit = Number(
            patient?.agent_insurance?.pay_to_visit ||
            patient?.pay_to_visit ||
            this.agentPayRate ||
            0
          );

          // Enriquecer cada visita con el pay_to_visit del agente
          const enrichedVisits = filteredVisits.map((visit) => {
            // Buscar pay_to_visit en la visita misma primero
            const visitPayToVisit = Number(
              visit?.pay_to_visit ||
              visit?.agent_insurance?.pay_to_visit ||
              0
            );
            
            // Usar el pay_to_visit de la visita si existe, sino usar agentPayToVisit del patient
            const finalPayToVisit = visitPayToVisit > 0 ? visitPayToVisit : agentPayToVisit;
            
            // Valor por defecto cuando no se encuentra pay_to_visit
            const DEFAULT_PAY_TO_VISIT = 3;
            
            return {
              ...visit,
              // Usar pay_to_visit si existe, sino usar valor por defecto (3) en lugar del total del backend
              total: finalPayToVisit > 0 ? finalPayToVisit : DEFAULT_PAY_TO_VISIT,
              pay_to_visit: finalPayToVisit,
            };
          });

          // Obtener el nombre del agente
          const agentName = patient?.agent_insurance
            ? [patient.agent_insurance.name, patient.agent_insurance.lastname]
                .filter(Boolean)
                .join(" ") || "Unknown Agent"
            : "Unknown Agent";

          return {
            ...patient,
            fullName: [patient.name, patient.lastname].filter(Boolean).join(" "),
            agentName: agentName,
            typeInsuranceLabel:
              this.getInsuranceNameById(patient.type_insurance) ||
              patient.type_insurance ||
              "Undefined",
            visits: enrichedVisits,
            visits_count: enrichedVisits.length,
            total_visit_amount: this.calculatePatientTotal(enrichedVisits),
          };
        })
        .filter(Boolean);
    },
    calculatePatientTotal(visits = []) {
      // Si las visitas ya están enriquecidas con pay_to_visit, sumar sus totales
      return visits.reduce(
        (acc, visit) => acc + (Number(visit.total) || 0),
        0
      );
    },
    getInsuranceNameById(id) {
      if (id === null || id === undefined) return null;
      return this.insuranceTypeMap[id] || null;
    },
    updateChartData() {
      const categories = this.visitCounts.map((item) => item.prefix);
      const data = this.visitCounts.map((item) => Number(item.count || 0));
      this.visitCountChartOptions = {
        ...this.visitCountChartOptions,
        xaxis: {
          ...this.visitCountChartOptions.xaxis,
          categories,
        },
      };
      this.visitCountChartSeries = [
        {
          name: "Visits",
          data,
        },
      ];
    },
    updatePatientsByInsuranceChart() {
      const grouped = (this.patientsByInsurance || [])
        .map((item) => {
          const patients = (Array.isArray(item.patients) ? item.patients : []).filter(
            (patient) =>
              this.isMembershipNP(patient.type_membership) ||
              this.isMembershipNC(patient.type_membership)
          );

          if (!patients.length) return null;

          return {
            name: item.type_insurance || "Undefined",
            np: patients.filter((patient) =>
              this.isMembershipNP(patient.type_membership)
            ).length,
            nc: patients.filter((patient) =>
              this.isMembershipNC(patient.type_membership)
            ).length,
          };
        })
        .filter(Boolean);

      if (!grouped.length) {
        this.patientsByInsuranceChartOptions = {
          ...this.patientsByInsuranceChartOptions,
          xaxis: {
            ...this.patientsByInsuranceChartOptions.xaxis,
            categories: [],
          },
        };
        this.patientsByInsuranceChartSeries = [
          { name: "New patients (NP)", data: [] },
          { name: "Returning patients (NC)", data: [] },
        ];
        return;
      }

      const categories = grouped.map((item) => item.name);
      const npData = grouped.map((item) => item.np);
      const ncData = grouped.map((item) => item.nc);

      this.patientsByInsuranceChartOptions = {
        ...this.patientsByInsuranceChartOptions,
        xaxis: {
          ...this.patientsByInsuranceChartOptions.xaxis,
          categories,
        },
      };

      this.patientsByInsuranceChartSeries = [
        { name: "New patients (NP)", data: npData },
        { name: "Returning patients (NC)", data: ncData },
      ];
    },
    normalizeMembership(value) {
      return (value || "").replace(/[^a-zA-Z]/g, "").toUpperCase();
    },
    isMembershipNP(value) {
      const normalized = this.normalizeMembership(value);
      return normalized === "NP" || normalized.endsWith("NP");
    },
    isMembershipNC(value) {
      const normalized = this.normalizeMembership(value);
      return normalized === "NC" || normalized.endsWith("NC");
    },
    filterMembershipCounts(counts = []) {
      return counts.filter((item) => {
        const normalized = this.normalizeMembership(item?.prefix);
        return (
          normalized === "NP" ||
          normalized === "NC" ||
          normalized.endsWith("NP") ||
          normalized.endsWith("NC")
        );
      });
    },
    formatCurrency(value) {
      const numericValue = Number(value) || 0;
      return new Intl.NumberFormat("es-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
      }).format(numericValue);
    },
    formatNumber(value, isInteger = false) {
      const numericValue = Number(value) || 0;
      return new Intl.NumberFormat("es-US", {
        minimumFractionDigits: isInteger ? 0 : 2,
        maximumFractionDigits: isInteger ? 0 : 2,
      }).format(numericValue);
    },
  },
  watch: {
    "filters.agent_id"(newValue, oldValue) {
      if (!this.shouldAutoFetch || newValue === oldValue) return;
      this.fetchDashboardData();
    },
    "filters.type_insurance"(newValue, oldValue) {
      if (!this.shouldAutoFetch || newValue === oldValue) return;
      this.fetchDashboardData();
    },
  },
  mounted() {
    Promise.all([this.loadAgentOptions(), this.loadInsuranceTypes()]).finally(
      () => {
        if (this.filters.agent_id) {
          this.shouldAutoFetch = true;
          this.fetchDashboardData();
        }
      }
    );
  },
};
</script>

<style scoped>
.admin-agent-dashboard {
  background-color: #f4f6f8;
  min-height: 100vh;
}

.summary-card {
  background-color: #ffffff;
  border: 1px solid #e4e9f2;
}

.summary-card .h4 {
  font-weight: 700;
}

.agent-data-table ::v-deep th {
  text-transform: capitalize;
}

.visit-totals-legend-item:last-child {
  margin-bottom: 0 !important;
}

.visit-totals-legend-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  display: inline-block;
}

@media (max-width: 767px) {
  .visit-totals-legend-item {
    justify-content: flex-start;
  }
}
</style>

